import {gql} from '@apollo/client';
import {DocumentNode} from 'graphql';

import {SchemaTag} from 'folder-items/FileConstants';
import {getApolloClient} from 'graphql-client/ApolloClient';
import {doQuerySafe, readFragmentFromCache} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {MixtureIngredientsStructuredTableRowMetadata} from 'notebook/integrations/registry/MixtureIngredientsStructuredTableAdapter';
import {ColumnGroup} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {LiveCellValue} from 'notebook/structured-tables/LiveCellValues';
import {
  SampleLookupInfo,
  StructuredTableColumn,
  StructuredTableRow,
  StructuredTableRowUpdater,
} from 'notebook/structured-tables/StructuredTableConstants';
import isColumnForSchemaField from 'notebook/structured-tables/util/isColumnForSchemaField';
import updateCellWithLiveValue from 'notebook/structured-tables/util/updateCellWithLiveValue';
import {TableColumnType} from 'notebook/tables/TableConstants';
import {convertTagToTypedValue} from 'notebook/tables/typed-values/DataTypes';

import {InaccessibleInfoGraphQL} from '../../../graphql-fragments/auth/__generated/InaccessibleInfoGraphQL';
import {
  BulkFetchProcessOutputQuery,
  BulkFetchProcessOutputQueryVariables,
} from './__generated/BulkFetchProcessOutputQuery';
import {ProcessOutputStructuredTableRowMetadata} from './ProcessOutputStructuredTableAdapter';

type ProcessOutput = ExtractByTypename<
  BulkFetchProcessOutputQuery['processOutputs'][number],
  'ProcessOutputNode'
>;

export const BULK_FETCH_PROCESS_OUTPUT_QUERY: DocumentNode = gql`
  query BulkFetchProcessOutputQuery($outputIds: [StrictString!]!) @skipAudit @engComponent(name: WORKFLOWS) {
    processOutputs(ids: $outputIds) {
      ...ProcessOutputRowGraphQL
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('ProcessOutputRow').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

async function _bulkFetchModel(
  sampleLookupInfos: Array<SampleLookupInfo>
): Promise<Array<ProcessOutput | InaccessibleInfoGraphQL>> {
  const {processOutputs} = await doQuerySafe<
    BulkFetchProcessOutputQuery,
    BulkFetchProcessOutputQueryVariables
  >(
    {query: BULK_FETCH_PROCESS_OUTPUT_QUERY},
    {
      variables: {
        outputIds: sampleLookupInfos.map((info) => info.idForLookup),
      },
      fetchPolicy: 'network-only',
    }
  );
  return processOutputs;
}

const ProcessOutputRowUpdater: StructuredTableRowUpdater<
  ProcessOutput,
  ProcessOutputStructuredTableRowMetadata
> = {
  fetchModel: (sampleLookupInfo: SampleLookupInfo): Promise<ProcessOutput | InaccessibleInfoGraphQL> =>
    _bulkFetchModel([sampleLookupInfo]).then((processOutputs) => processOutputs[0]),

  bulkFetchModel(
    sampleLookupInfos: Array<SampleLookupInfo>
  ): Promise<Array<ProcessOutput | InaccessibleInfoGraphQL>> {
    return _bulkFetchModel(sampleLookupInfos);
  },

  updateCell: (
    output: ProcessOutput | InaccessibleInfoGraphQL,
    columnGroup: ColumnGroup | null,
    column: StructuredTableColumn
  ): LiveCellValue => {
    if (isInaccessible(output)) {
      return 'NO_VALUE';
    }
    switch (column.columnType) {
      case TableColumnType.PROCESS_OUTPUT:
        // Outputs are rendered with their humanId, which is not subject to staleness
        return 'NO_VALUE';

      case TableColumnType.PROCESS_TASK:
        // Tasks are rendered with their humanId, which is not subject to staleness
        return 'NO_VALUE';

      default: {
        const matchingTag = Array.isArray(output.fields)
          ? output.fields.find((tag: SchemaTag) => isColumnForSchemaField(column, tag.tagSchemaField))
          : null;
        if (matchingTag) {
          return convertTagToTypedValue(matchingTag);
        }

        return 'NO_VALUE';
      }
    }
  },

  updateRow(
    output: ProcessOutput | InaccessibleInfoGraphQL,
    columns: Array<StructuredTableColumn>,
    row: StructuredTableRow<ProcessOutputStructuredTableRowMetadata>,
    columnGroup: ColumnGroup | null
  ): StructuredTableRow<MixtureIngredientsStructuredTableRowMetadata> {
    if (isInaccessible(output)) {
      return row;
    }

    const cells = row.cells.map((cell, columnIndex) => {
      const liveValue = this.updateCell(output, columnGroup, columns[columnIndex]);
      return updateCellWithLiveValue(cell, liveValue, {setCellBlankIfLiveValueNull: true});
    });

    return {
      ...row,
      cells,
      lastSyncedArchivePurpose: output.archiveRecord?.purpose ?? null,
    };
  },

  readFromCache: ({idForLookup: apiIdentifier}: SampleLookupInfo): ProcessOutput | null =>
    readFragmentFromCache(getFragmentWrapper('ProcessOutputRow'), apiIdentifier),
};

export function addToProcessOutputRowUpdaterCache(outputs: Array<ProcessOutput>): void {
  const apolloClient = getApolloClient();
  apolloClient.writeQuery({
    query: BULK_FETCH_PROCESS_OUTPUT_QUERY,
    data: {processOutputs: outputs},
    variables: {outputId: outputs.map((output) => output.id)},
  });
}

export default ProcessOutputRowUpdater;
